import { createInventoryItem, resolveItemTypeToMeta } from "game-common/item/item"
import { Coordinate, Inventory, TileSize } from "game-common/models"
import { BaseMapBuilder, MapEntry } from "game-common/room/base_map_builder"
import { forestGrassTile } from "game-common/room/ember/grass_art"
import { houseRoofOffset } from "game-common/room/ember/two_storey_house"
import { GridCell, HouseBuilder } from "game-common/room/house/feature/house"
import { blankMap } from "game-common/util"
import { BLEND_MODES, Container, Graphics, Sprite, Texture } from "pixi.js"
import { contentSheetResource } from "../content_textures"
import { BuildCursor, BuildPlacementFeedback, drawRoofLiftGuide } from "../effects/build_placement_feedback"
import { GuiManager } from "../gui_manager"
import { FireSprite } from "../sprites/fire_sprite"
import { SCALER } from "../../../../client_util"
import { BuildingPrintCountdown } from "./building_print_countdown"
import { glowDisc, shineBand, sparkBurst, REWARD_GOLD, REWARD_GOLD_HOT } from "./chapter_reward_art"
import { easeInCubic, easeInOutCubic, easeOutCubic, lerp, quadPoint, span } from "./chapter_reward_motion"
import { EquippedWidget, SLOT, SLOT_PITCH, TRAY_HEIGHT, TRAY_PAD } from "./equipped"
import { FlameBackdrop } from "./flame_backdrop"
import { DemoCursor, DemoStamp, drawDioramaFrame, drawRipples, Pt, TutorialDemo } from "./tutorial_demo"

export { raisedStampPose } from "./tutorial_demo"

// The building helper: a little diorama of the camp in which a pointer readies roof tiles, drags a square
// over the whole shelter and lets go, and the roof goes up. It is a lesson the player watches rather
// than a form they read, so it is drawn in the camp HUD's own materials (build_board's timber and
// bronze, the clues' gold spark, the chapter checklist's diamonds and stamp) rather than as a panel of
// numbered sentences.
//
// Everything inside the frame is the REAL thing: the ready bar is the HUD's EquippedWidget; the walls
// and the roof are laid out by the camp's own HouseBuilder, so every wall cap, post and roof edge is
// the autotile a player's build gets, and the roof is raised off its cells by houseRoofOffset() as the
// map draws every roof; the hearth is the campfire's tiles and flame; and the cursor, the drag, the
// counting halo and the build are drawn by the game's own build cursor, BuildPlacementFeedback and
// BuildingPrintCountdown -- so what the demo shows is exactly what the player will see when they try
// it. Only the chrome around it (step track, pointer, the closing flourish) belongs to the demo; the
// window itself is tutorial_demo.ts.

// The board, and the diorama inside it. CELL must equal TileSize: the placement feedback draws in
// world tiles, and the scene is laid out on the same grid at 1:1.
const CELL = 32
const COLS = 10, ROWS = 8
const SCENE_W = COLS * CELL, SCENE_H = ROWS * CELL
const WIDTH = SCENE_W + 44
const SCENE_X = (WIDTH - SCENE_W) / 2
const SCENE_Y = 92
const CAPTION_Y = SCENE_Y + SCENE_H + 13
const CAPTION_H = 36
const HEIGHT = CAPTION_Y + CAPTION_H + 10

// The starter shelter (EmberRoomService.ruinedWallCells): a 5×5 wooden perimeter round the hearth with
// a one-tile doorway east of centre on its south side. The roof chapter two asks for covers all of it,
// walls included. Clear of the ready bar to its west; the rows above it hold the north wall's caps,
// the print dial and the stamp, all of which stand higher than the cells they belong to.
const HEARTH = { x: 6, y: 5 }
const SHELTER_RADIUS = 2
const DOOR = { x: HEARTH.x + 1, y: HEARTH.y + SHELTER_RADIUS }
const ROOF_SIDE = SHELTER_RADIUS * 2 + 1, ROOF_TILES = ROOF_SIDE * ROOF_SIDE
const ROOF_LEFT = HEARTH.x - SHELTER_RADIUS, ROOF_TOP = HEARTH.y - SHELTER_RADIUS
const ROOF_W = ROOF_SIDE * CELL, ROOF_H = ROOF_SIDE * CELL
// The campfire's three composite tiles (lpc-terrain:760 is the one a placed campfire names), and the
// camp's flame standing in its logs -- as the forager's helper draws it.
const FIRE_X = (HEARTH.x + 0.5) * CELL
const FIRE_BASE_Y = HEARTH.y * CELL + 20
const FLAME_W = 34, FLAME_H = 44
// Firelight is ADDED over grass, so it is starved of green: a gold glow there turns lime.
const FIRELIGHT = 0xff3d08
// Player.buildGhostAlpha: a pending ghost at BUILD_GHOST_ALPHA that firms toward 0.7 as it counts down.
const GHOST_ALPHA = 0.4, GHOST_FIRM_ALPHA = 0.7
// World stacking, as the map uses it: a cell's own sprites by their row, then the build overlays.
const rowZ = (y: number) => 100 + y * CELL + CELL / 3
const GHOST_Z = 100000, CURSOR_Z = 100000.1, GUIDE_Z = 100001

const cellKey = ({ x, y }: Coordinate) => `${x},${y}`
/** The top-left `side`×`side` of the roof: what a drag has marked when its corner is that far along. */
const roofCells = (side: number): Coordinate[] => {
    const cells: Coordinate[] = []
    for (let y = 0; y < side; y++) for (let x = 0; x < side; x++) cells.push({ x: ROOF_LEFT + x, y: ROOF_TOP + y })
    return cells
}
const shelterWalls = (): Coordinate[] => roofCells(ROOF_SIDE).filter(({ x, y }) =>
    (x === ROOF_LEFT || x === ROOF_LEFT + ROOF_SIDE - 1 || y === ROOF_TOP || y === ROOF_TOP + ROOF_SIDE - 1) &&
    !(x === DOOR.x && y === DOOR.y))

/** The map entries the camp's builder lays for these walls and this roof (house_roof.ts does the same). */
const shelterEntries = (walls: Coordinate[], roof: Coordinate[]): MapEntry[] => {
    // The builder reads its grids [y][x]; a square grid keeps blankMap's [x][y] from mattering.
    const grid = () => blankMap<GridCell>(COLS, COLS)
    const house = grid(), roofGrid = grid()
    walls.forEach(({ x, y }) => { house[y][x] = { itemType: "wall-wood" } })
    roof.forEach(({ x, y }) => { roofGrid[y][x] = { itemType: "roof-wood" } })
    const builder = new HouseBuilder({} as BaseMapBuilder, {
        blockingTileSpec: { tilePos: 121, tilesetId: "lpc-terrain" },
        house, roof: roofGrid, entity: grid(), floor: grid(), floor2: grid(), decor: grid(),
    })
    builder.build("building-tutorial")
    return builder.featureSpec.mapEntries
}
// A map entry's tilePos is 0-based within its tileset; the atlas names that cell frame tilePos + 1
// (TileMapMetaProvider.resolveTilesetTileId).
const atlasFrame = (entry: MapEntry) => entry.tileSpec.tilePos + 1

// ---- The timeline ------------------------------------------------------------------------------
//
// Each step animates, then holds until Next. Back restarts the preceding step, and Replay returns
// to the first. Everything that moves inside it is a pure function of its clock, so any instant can be
// posed exactly; only effects that run on after an event (sparks, the settle) use the monotonic
// frame clock, which keeps running while the play-through holds.

export const BUILDING_DEMO_MS = 8000
const SELECT_AT = 1000 // the pointer clicks the roof tiles on the ready bar
const DRAG_FROM = 1900 // ...and heads for the ground
const PRESS_AT = 2550
const DRAG_START = 2650, DRAG_END = 3750
const RELEASE_AT = 3950
const PRINT_MS = 2700
const BUILT_AT = RELEASE_AT + PRINT_MS
const STAMP_AT = BUILT_AT + 250
const DONE_AT = BUILT_AT + 450
const REPLAY_AT = 7400
const RIPPLES = [SELECT_AT, PRESS_AT, RELEASE_AT]

const CAPTIONS = [
    "Pick the roof tiles from your ready bar.",
    "Drag across the whole shelter to highlight a 5×5 square.",
    "Let go and wait. All 25 roof tiles build at once.",
]
const DONE_CAPTION = "Your roof is up! Now cover your whole shelter."

/** Where the demonstration is at `elapsedMs` into one play-through. */
export const buildingTutorialPose = (elapsedMs: number) => {
    const t = Math.min(BUILDING_DEMO_MS, Math.max(0, elapsedMs))
    const drag = easeInOutCubic(span(t, DRAG_START, DRAG_END))
    const firm = span(t, RELEASE_AT, BUILT_AT)
    return {
        step: t < DRAG_FROM ? 0 : t < RELEASE_AT ? 1 : 2,
        selected: t < SELECT_AT ? 0 : 1,
        drag,
        // A cell lights when the POINTER crosses into it (halfway between two cell centres), not on
        // a timer, so the highlight is seen to follow the hand.
        tiles: t < PRESS_AT ? 0 : Math.pow(1 + Math.floor(drag * (ROOF_SIDE - 1) + 0.5), 2),
        released: t >= RELEASE_AT,
        built: t >= BUILT_AT,
        done: t >= DONE_AT,
        // The pending roof's ghost: steady while it is drawn, firming as its countdown runs.
        ghostAlpha: lerp(GHOST_ALPHA, GHOST_FIRM_ALPHA, firm * firm * (3 - 2 * firm)),
    }
}

export interface CursorStops { rest: Pt; slot: Pt; start: Pt; end: Pt }

// A hand moves in arcs, not along a ruler.
const glide = (from: Pt, to: Pt, k: number) =>
    quadPoint(from, { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 18 }, to, easeInOutCubic(k))

/** The pointer: where it is, whether it is held down, how visible it is. */
export const buildingCursorPose = (elapsedMs: number, at: CursorStops): DemoCursor => {
    const t = Math.min(BUILDING_DEMO_MS, Math.max(0, elapsedMs))
    let p: Pt
    if (t < DRAG_FROM) p = glide(at.rest, at.slot, span(t, 150, 900))
    else if (t < PRESS_AT) p = glide(at.slot, at.start, span(t, DRAG_FROM, PRESS_AT - 80))
    else if (t < RELEASE_AT) p = { x: lerp(at.start.x, at.end.x, buildingTutorialPose(t).drag), y: lerp(at.start.y, at.end.y, buildingTutorialPose(t).drag) }
    else {
        // Let go, then step back out of the way of the dial and the build.
        const k = easeInOutCubic(span(t, RELEASE_AT + 120, RELEASE_AT + 700))
        p = { x: at.end.x + 26 * k, y: at.end.y + 6 * k }
    }
    return {
        ...p,
        pressed: (t >= SELECT_AT && t < SELECT_AT + 170) || (t >= PRESS_AT && t < RELEASE_AT),
        alpha: Math.min(easeOutCubic(span(t, 0, 250)), 1 - easeInCubic(span(t, RELEASE_AT + 250, RELEASE_AT + 750))),
    }
}

interface SceneArt { sprite: Sprite; sheet: string; tile: number }
interface RoofArt { sprite: Sprite; sheet: string; cell: Coordinate; frames: (number | undefined)[] }

/** Real game controls and atlas tiles in an isolated scene. All control callbacks are local. */
export class BuildingTutorial extends TutorialDemo {
    private scene = new Container()
    private frame = new Graphics()
    private fx = new Graphics()
    private hearthGlow = new Graphics()
    private flame: FireSprite | FlameBackdrop
    private stamp = new DemoStamp("ROOF RAISED")
    private readyBar: EquippedWidget
    private countdown: BuildingPrintCountdown
    private feedback: BuildPlacementFeedback
    // The game's build cursor over the pointer's cell, up on the roof's plane, and the dotted rise from
    // the ground cell to it.
    private target = new BuildCursor(() => this.clock)
    private liftGuide: Graphics
    private inventory: Inventory = { items: ["axe", "roof-wood"].map((type: any, i) => ({
        ...createInventoryItem(resolveItemTypeToMeta(type), { id: `tutorial-${type}`, quantity: i === 1 ? ROOF_TILES : 1 }),
        readied: true, active: i === 0,
    })), abilities: {}, lastUpdateTs: 0 }
    // Grass, walls and the campfire: textured from their atlases once those have loaded.
    private art: SceneArt[] = []
    // The roof's cells, each with its autotile for every square the drag passes through (1×1 up to
    // 5×5): the ghosts follow the drag as the real previews do, re-autotiled as the square grows.
    private roof: RoofArt[] = []
    // The roof is drawn this far off its cells (negative: up), as the map draws every roof.
    private lift = houseRoofOffset()
    // What the play-through has already told the placement feedback, so each event fires once.
    private played = { tiles: 0, released: false, built: false }
    private selected = -1

    constructor(hover: (over: boolean) => void, dismiss: () => void) {
        super({
            width: WIDTH, height: HEIGHT, padX: SCENE_X, contentWidth: SCENE_W,
            captionY: CAPTION_Y, captionH: CAPTION_H, overline: "BUILDING", title: "Build a 5×5 roof",
            steps: {
                labels: ["PICK", "DRAG", "BUILD"],
                litAt: [0, DRAG_FROM, RELEASE_AT + 420],
                doneAt: [SELECT_AT, RELEASE_AT, BUILT_AT],
                linkFill: [[SELECT_AT + 120, DRAG_FROM], [RELEASE_AT + 60, RELEASE_AT + 420]],
            },
            durationMs: BUILDING_DEMO_MS, replayAt: REPLAY_AT,
            manualStepStarts: [0, DRAG_FROM, RELEASE_AT],
        }, hover, dismiss)

        // The diorama. Sortable, so everything keeps its world stacking: grass, then the walls and the
        // fire by row, then the roof, the cursor, the placement feedback and the print dial over all.
        this.scene.position.set(SCENE_X, SCENE_Y)
        this.scene.sortableChildren = true
        this.content.addChild(this.scene)
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
            this.addTile("forest-grass", forestGrassTile(x, y), x, y, 0)
        }
        this.hearthGlow.blendMode = BLEND_MODES.ADD
        this.hearthGlow.zIndex = 1
        this.scene.addChild(this.hearthGlow)
        ;[759, 760, 761].forEach((tile, i) => this.addTile("lpc-terrain", tile, HEARTH.x - 1 + i, HEARTH.y, rowZ(HEARTH.y)))
        this.flame = this.buildFlame()
        this.flame.zIndex = rowZ(HEARTH.y) + 1
        this.scene.addChild(this.flame)
        shelterEntries(shelterWalls(), []).filter(entry => entry.layerId === "sprite").forEach(entry =>
            this.addTile(entry.tileSpec.tilesetId, atlasFrame(entry), entry.x, entry.y, rowZ(entry.y)))
        this.buildRoof()

        this.target.zIndex = CURSOR_Z
        this.liftGuide = drawRoofLiftGuide(new Graphics(), this.lift)
        this.liftGuide.zIndex = GUIDE_Z
        this.target.visible = this.liftGuide.visible = false
        this.scene.addChild(this.target, this.liftGuide)
        this.feedback = new BuildPlacementFeedback(() => this.scene, () => this.clock)
        this.countdown = new BuildingPrintCountdown(() => this.scene)
        // At 2x the dial is drawn the size it is on screen in the camp, so its seconds can be read in
        // the little window. Its root sits half a tile into its group's anchor; put that root where
        // Player.syncBuildingPrintGroups puts a rectangle's dial: over the middle of the top edge, a
        // row above it, on the roof's plane.
        this.countdown.scale.set(2, 2 * SCALER)
        const dialX = (HEARTH.x + 0.5) * CELL, dialY = (ROOF_TOP - 1 + 0.5) * CELL + this.lift
        this.countdown.position.set(dialX - TileSize, dialY - TileSize * SCALER)
        this.scene.addChild(this.countdown)
        drawDioramaFrame(this.frame, SCENE_X, SCENE_Y, SCENE_W, SCENE_H)
        this.content.addChild(this.frame)

        // The ready bar where the HUD keeps it: docked in the bottom-left corner of the view.
        this.readyBar = new EquippedWidget({} as GuiManager)
        this.readyBar.position.set(SCENE_X + 6, SCENE_Y + SCENE_H - TRAY_HEIGHT - 6)
        this.content.addChild(this.readyBar)

        this.fx.blendMode = BLEND_MODES.ADD
        this.content.addChild(this.fx)
        // Centred over the roof, in the free rows above it.
        this.stamp.place(SCENE_X + FIRE_X, SCENE_Y + (ROOF_TOP * CELL + this.lift) / 2)
        this.content.addChild(this.stamp.burst, this.stamp.plaque)
        this.animate(0)
    }

    private addTile = (sheet: string, tile: number, x: number, y: number, zIndex: number) => {
        const sprite = new Sprite(Texture.EMPTY)
        sprite.position.set(x * CELL, y * CELL)
        sprite.width = CELL; sprite.height = CELL
        sprite.zIndex = zIndex
        this.scene.addChild(sprite)
        this.art.push({ sprite, sheet, tile })
    }

    private buildRoof = () => {
        const squares = Array.from({ length: ROOF_SIDE }, (_, i) => new Map(shelterEntries([], roofCells(i + 1))
            .filter(entry => entry.layerId === "sky").map(entry => [cellKey(entry), entry])))
        roofCells(ROOF_SIDE).forEach(cell => {
            const entries = squares.map(square => square.get(cellKey(cell)))
            const sprite = new Sprite(Texture.EMPTY)
            sprite.position.set(cell.x * CELL, cell.y * CELL + this.lift)
            sprite.width = CELL; sprite.height = CELL
            sprite.zIndex = GHOST_Z
            sprite.visible = false
            this.scene.addChild(sprite)
            this.roof.push({ sprite, cell, sheet: entries[ROOF_SIDE - 1]?.tileSpec.tilesetId ?? "fence_medieval",
                frames: entries.map(entry => entry && atlasFrame(entry)) })
        })
    }

    private buildFlame = (): FireSprite | FlameBackdrop => {
        try {
            const fire = new FireSprite()
            // FireSprite seats its coals 3px left of its origin, as it would above a placed campfire;
            // land them on the logs' crossing (the pit art's row 16).
            fire.position.set(FIRE_X + 3, FIRE_BASE_Y - 4)
            return fire
        } catch {
            // If the real flame can't be built, the roster card's live one stands in.
            const flame = new FlameBackdrop(FLAME_W, FLAME_H, { glow: false })
            flame.position.set(FIRE_X - FLAME_W / 2, FIRE_BASE_Y - FLAME_H)
            return flame
        }
    }

    private dress = (sprite: Sprite, sheet: string, tile: number) => {
        const texture = contentSheetResource(sheet)?.textures?.[`${sheet}-${tile}`] || Texture.EMPTY
        if (sprite.texture !== texture) { sprite.texture = texture; sprite.width = CELL; sprite.height = CELL }
    }

    protected rewind() {
        this.feedback.clear()
        this.played = { tiles: 0, released: false, built: false }
    }

    protected poseScene(t: number, deltaMs: number) {
        const pose = buildingTutorialPose(t)

        if (this.selected !== pose.selected) {
            this.selected = pose.selected
            this.inventory.items.forEach((item, i) => item.active = i === pose.selected)
            this.readyBar.update(this.inventory, () => {})
        }
        this.readyBar.animate(deltaMs)

        this.art.forEach(({ sprite, sheet, tile }) => this.dress(sprite, sheet, tile))
        // The real print queue shows ghost roof tiles until its countdown commits the batch.
        const side = Math.sqrt(pose.tiles)
        this.roof.forEach(({ sprite, sheet, frames }) => {
            const frame = frames[side - 1]
            sprite.visible = frame !== undefined
            if (frame !== undefined) this.dress(sprite, sheet, frame)
            sprite.alpha = pose.built ? 1 : pose.ghostAlpha
        })
        this.poseHearth(deltaMs)
        this.poseCursor(t)
        this.playFeedback(pose)

        if (pose.released && !pose.built) {
            this.countdown.setGroups([{ id: 1, anchor: { x: 0, y: 0 }, startedAt: RELEASE_AT, duration: PRINT_MS,
                count: ROOF_TILES, onCancel: () => {} }], t)
        } else this.countdown.setGroups([], t)

        this.drawFx(t)
        this.stamp.draw(t - STAMP_AT)
    }

    protected cursorAt(t: number) {
        return buildingCursorPose(t, this.cursorStops())
    }

    protected captionAt(t: number) {
        const pose = buildingTutorialPose(t)
        return { text: pose.done ? DONE_CAPTION : CAPTIONS[pose.step], done: pose.done }
    }

    /** A burning fire, its light spilling out of the doorway and from under the eaves once roofed. */
    private poseHearth = (deltaMs: number) => {
        if (this.flame instanceof FireSprite) this.flame.setIntensity(0.6)
        else this.flame.update(deltaMs || 0, 0.6)
        const flicker = 0.5 + 0.5 * Math.sin(this.clock / 170) * Math.sin(this.clock / 310)
        glowDisc(this.hearthGlow.clear(), FIRE_X, FIRE_BASE_Y - 4, 44, FIRELIGHT, 0.16 + 0.05 * flicker, 5)
    }

    /**
     * Player.updateBuildGridMarker, for the pointer: once the roof tiles are in hand, the build cursor
     * outlines the pointer's cell up on the roof's plane, gliding cell to cell and breathing while it waits,
     * with the dotted rise down to the ground under it until a drag begins.
     */
    private poseCursor = (t: number) => {
        const pointer = this.cursorAt(t)
        const x = Math.floor((pointer.x - SCENE_X) / CELL), y = Math.floor((pointer.y - SCENE_Y) / CELL)
        const bar = this.readyBar
        const overBar = pointer.x < bar.x + bar.width + 4 && pointer.y > bar.y - 4
        const show = buildingTutorialPose(t).selected === 1 && pointer.alpha > 0 && !overBar &&
            x >= 0 && y >= 0 && x < COLS && y < ROWS
        this.target.visible = show
        this.liftGuide.visible = show && !pointer.pressed && t < RELEASE_AT
        if (show) {
            this.target.moveTo(x * CELL, y * CELL + this.lift)
            this.target.alpha = pointer.alpha
            this.liftGuide.position.set((x + 0.5) * CELL, (y + 0.5) * CELL)
        }
        this.target.animate()
        this.liftGuide.pivot.set(-this.target.body.x, -this.target.body.y)
        this.liftGuide.alpha = this.target.body.alpha
    }

    /** Tell the game's own placement feedback what a real drag would have, once per event. */
    private playFeedback = (pose: ReturnType<typeof buildingTutorialPose>) => {
        if (pose.tiles > this.played.tiles && !pose.released) {
            this.played.tiles = pose.tiles
            // Each step of the drag stamps the cursor, as Player.playBuildSwing does.
            this.target.press()
            const side = Math.sqrt(pose.tiles)
            // Each newly marked cell, on the roof's plane, approved as soon as its preview returns...
            roofCells(side).forEach(cell => {
                this.feedback.add(cellKey(cell), cell, this.lift)
                this.feedback.setState(cellKey(cell), "valid")
            })
            // ...and the rectangle's pinned corner, as a rectangle drag draws it.
            this.feedback.setAreaFootprint({ x: ROOF_LEFT, y: ROOF_TOP },
                { x: ROOF_LEFT + side - 1, y: ROOF_TOP + side - 1 }, {}, this.lift)
        }

        if (pose.released && !this.played.released) {
            this.played.released = true
            this.feedback.clearAreaFootprint()
            roofCells(ROOF_SIDE).forEach(cell => {
                this.feedback.add(cellKey(cell), cell, this.lift, false)
                this.feedback.setState(cellKey(cell), "valid")
                this.feedback.setCounting(cellKey(cell), this.clock, 1)
            })
        }
        if (pose.built && !this.played.built) {
            this.played.built = true
            roofCells(ROOF_SIDE).forEach(cell => {
                this.feedback.remove(cellKey(cell))
                this.feedback.spark(cell, "built", this.lift)
            })
            this.feedback.settle(this.roof.map(tile => tile.sprite))
        }
        this.feedback.update()
    }

    /** The light over the scene: the roof catching it as it goes up. */
    private drawFx = (t: number) => {
        const g = this.fx.clear()
        drawRipples(g, t, RIPPLES, at => buildingCursorPose(at, this.cursorStops()))
        if (t < BUILT_AT) return
        const ms = t - BUILT_AT
        const x = SCENE_X + ROOF_LEFT * CELL, y = SCENE_Y + ROOF_TOP * CELL + this.lift
        const cx = x + ROOF_W / 2, cy = y + ROOF_H / 2
        // One warm bloom as the roof commits, then a band of light across the finished roof -- inside
        // its ragged plank ends, which have grass between them that ADD would turn lime.
        const bloom = 1 - easeOutCubic(span(ms, 0, 700))
        if (bloom > 0) glowDisc(g, cx, cy, ROOF_W * 0.6, REWARD_GOLD_HOT, 0.6 * bloom, 5)
        shineBand(g, x + 4, y + 2, ROOF_W - 8, ROOF_H - 6, easeInOutCubic(span(ms, 60, 760)), 26, 0.5)
        sparkBurst(g, cx, cy, span(ms, 0, 650), 14, ROOF_W * 0.3, ROOF_W * 0.75, REWARD_GOLD, 0.4, 1.75)
    }

    // The drag runs over the GROUND cells, corner to corner across the shelter; the roof it marks is
    // drawn up on its raised plane, which is what the cursor's dotted rise explains.
    private cursorStops = (): CursorStops => {
        const bar = this.readyBar
        return {
            rest: { x: SCENE_X + 9.3 * CELL, y: SCENE_Y + 4.6 * CELL },
            slot: { x: bar.x + TRAY_PAD + SLOT_PITCH + SLOT * 0.55, y: bar.y + TRAY_PAD + SLOT * 0.5 },
            start: { x: SCENE_X + (ROOF_LEFT + 0.5) * CELL, y: SCENE_Y + (ROOF_TOP + 0.5) * CELL },
            end: { x: SCENE_X + (ROOF_LEFT + ROOF_SIDE - 0.5) * CELL, y: SCENE_Y + (ROOF_TOP + ROOF_SIDE - 0.5) * CELL },
        }
    }

    // First: the feedback destroys its own graphics, which the children sweep would otherwise
    // destroy a second time.
    protected teardown() {
        this.feedback.destroy()
        // The real flame plays on the shared ticker; its baked frames are shared and stay.
        if (this.flame instanceof FireSprite) this.flame.cleanup()
    }
}
